import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import get from 'lodash-es/get'
import orderBy from 'lodash-es/orderBy'
import moment from 'moment'
import { hasPlusVideo, isVideoPlanCode, videoBannerTitle, videoPrice } from 'src/utils'
import { DashboardQueries, User, Account, Customer } from './dashboard.queries'
import { customerInContext } from 'src/logic/customer-states'
import { IPurchase } from 'src/graphql/queries/keyword.queries'
import { MatDialog } from '@angular/material/dialog'
import { TenTopicsPurchaseModal } from 'src/modules/keywords/modals/ten-topics-purchase-modal.component'
import { CustomerState } from 'src/types'
import { centsToCurrencyString } from 'src/logic/pricing'
import { EditspaceService } from 'src/services/editspace.service'

@Component({
  templateUrl: './dashboard-page.component.html'
})
export class DashboardPage implements OnInit {
  loading: boolean;
  customer: Customer;
  currentUser: User;
  account: Account;
  hubspotReminderDismissed: Object = {};
  toasts: JQuery = null;
  showSidebar: boolean;
  showBanner = false;
  canPurchaseTenTopics = true;
  lastTenTopicsPurchaseDate: moment.Moment;
  checkBackDate: moment.Moment;
  tenTopicsPrice: number;
  tenTopicPriceDefault: number;
  customerId: number = null;
  isSubmissionUI: boolean;
  modalVisible = false;
  isEditspace: boolean

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private queries: DashboardQueries,
    private dialog: MatDialog,
    private editspaceService: EditspaceService
  ) {
    this.router.events.subscribe(events => {
      if (events instanceof NavigationEnd) {
        this.isSubmissionUI = window.location.pathname.split('/').includes('submissions')
      }
    })
  }

  ngOnInit(): void {
    this.isEditspace = this.editspaceService.isEditspace()
    this.route.params.subscribe(params => {
      const customerId = +params['customerId']
      this.customerId = customerId
      // Newer data fetching via graphql. Eventually migrate the above into this
      if (customerId) {
        this.queries.dashboardQuery({ customerId }).subscribe(({currentUser, account, customer, loading}) => {
          this.loading = loading
          this.currentUser = currentUser
          this.account = account
          this.customer = customer
          this.showSidebar = currentUser?.showSidebar
          this.showBanner = this.getBannerVisibility()
          this.checkPurchases(customer?.extraPurchases)
          this.tenTopicsPrice = account?.tenTopicsPrice
          this.tenTopicPriceDefault = account?.tenTopicPriceDefault
        })
      } else {
        this.showSidebar = false
      }
    })
  }

  get header(): string {
    return this.account && this.account.agency ? 'New Client' : 'New Subscription'
  }

  get showNewVideoCustomerModal(): boolean {
    if (this.currentUser && this.currentUser.adminImpersonating) return false
    if (localStorage.getItem('dashboardPage:newVideoCustomerModalDismissed') === 'true') return false
    else {
      const videoSubsCount = this.account ? this.account.currentVideoSubscriptionsCount : null
      const requestsCount = this.customer ? this.customer.requestsCount : null
      const isVideoCustomer = isVideoPlanCode(get(this.customer, 'subscription.plan.code'))
      return requestsCount === 0 && videoSubsCount === 1 && isVideoCustomer
    }
  }

  get showVideoBanner(): boolean {
    const planCode = get(this.customer, 'subscription.plan.code', null)
    const isVideoCustomer = hasPlusVideo(this.customer) || (planCode && isVideoPlanCode(planCode))
    return this.customerInContext('current') &&
           !hasPlusVideo(this.customer) &&
           !isVideoCustomer &&
           this.currentUser && this.currentUser.canCreateVideoRequest
  }

  get videoPrice(): number {
    return videoPrice(this.customer)
  }

  dismissNewVideoCustomerModal(): void {
    localStorage.setItem('dashboardPage:newVideoCustomerModalDismissed', 'true')
  }

  navigateToNewVideoRequest(): void {
    this.dismissNewVideoCustomerModal()
    this.router.navigate(['/dashboard', this.customer.id, 'topics', 'new', 99])
  }

  onOpenPurchaseTopicsModal(): void {
    const dialogRef = this.dialog.open(TenTopicsPurchaseModal, {
      width: '600px',
      data: {
        price: this.tenTopicsPrice,
        priceDefault: this.tenTopicPriceDefault,
        customerId: this.customerId
      }
    })

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        this.checkPurchases(result.data)
      }
    })
  }

  videoBannerTitle(price: string): string {
    return videoBannerTitle(price)
  }

  prettyPrice(cents: number): string {
    return centsToCurrencyString(cents, { truncateZeroCents: true })
  }

  private checkPurchases(purchases: IPurchase[] = []): void {
    const tenTopics = purchases.filter(p => p.extra.code === 'ten_topics')
    const mostRecentPurchase = orderBy(tenTopics, t => new Date(t.purchasedAt), ['desc'])[0] || null

    if (mostRecentPurchase && this.checkPurchaseDate(mostRecentPurchase.purchasedAt) <= 48) {
      this.canPurchaseTenTopics = false
      this.checkBackDate = moment(mostRecentPurchase.purchasedAt).add(2, 'days')
    } else {
      this.canPurchaseTenTopics = true
      this.checkBackDate = null
    }
    this.lastTenTopicsPurchaseDate = mostRecentPurchase && moment(mostRecentPurchase.purchasedAt)
  }

  private checkPurchaseDate(dateStr: Date): number {
    const purchaseDate = moment(dateStr)
    const today = moment()
    return today.diff(purchaseDate, 'hours')
  }

  private getBannerVisibility(): boolean {
    if (this.customer) {
      return customerInContext(this.customer, 'current') && !this.isVideoSubscription()
    }
  }

  private isVideoSubscription(): boolean {
    if (this.customer && this.customer.subscription.plan) {
      return isVideoPlanCode(this.customer.subscription.plan.code)
    }
    return false
  }

  private customerInContext(context: CustomerState | 'current'): boolean {
    return this.customer && customerInContext(this.customer, context)
  }
}
